import React, { useState, useRef, useEffect } from "react";
import { createSession, deleteSession } from "excalidraw-app/storage/database";
import { uuid4 } from "@sentry/core";
import type { SessionType } from "excalidraw-app/app-language/session-state";
import { ErrorDialog } from "@excalidraw/excalidraw/components/ErrorDialog";
import Spinner from "@excalidraw/excalidraw/components/Spinner";

import "excalidraw-app/app-language/SessionManager.scss";

export const SessionManager: React.FC<{
  sessionName: string;
  setSessionName: (session: string) => void;
  sessions: SessionType[];
  updateSessionsFromDatabase: () => Promise<void> | void;
}> = ({
  sessionName,
  setSessionName,
  sessions,
  updateSessionsFromDatabase,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isCreating, setIsCreating] = useState(false);
  const [deletingSessionName, setDeletingSessionName] = useState<string | null>(
    null,
  );
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleCreateSession = async () => {
    const newSessionName = searchQuery.trim();
    if (newSessionName.length === 0) {
      setErrorMessage("Session name must not be empty");
      return;
    }
    setIsCreating(true);
    try {
      await createSession({
        [newSessionName]: {
          sessionName: newSessionName,
          sessionId: uuid4(),
          content: "[]",
        },
      });
      await updateSessionsFromDatabase();
      setSessionName(newSessionName);
      setSearchQuery("");
      setIsOpen(false);
    } catch (e: any) {
      setErrorMessage(`Create session failed: ${e.message}`);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteSession = async (
    e: React.MouseEvent,
    session: SessionType,
  ) => {
    e.stopPropagation();
    if (sessions.length <= 1) {
      setErrorMessage("Cannot delete the last session");
      return;
    }
    setDeletingSessionName(session.sessionName);
    try {
      await deleteSession(session.sessionName);
      await updateSessionsFromDatabase();
      // If the deleted session was active, switch to the first available
      if (sessionName === session.sessionName) {
        const remaining = sessions.filter(
          (s) => s.sessionName !== session.sessionName,
        );
        if (remaining.length > 0) {
          setSessionName(remaining[0].sessionName);
        }
      }
    } catch (err: any) {
      setErrorMessage(`Delete session failed: ${err.message}`);
    } finally {
      setDeletingSessionName(null);
    }
  };

  const filteredSessions = sessions.filter((s) =>
    s.sessionName.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const exactMatchExists = sessions.some(
    (s) => s.sessionName.toLowerCase() === searchQuery.trim().toLowerCase(),
  );

  return (
    <>
      <div
        className="session-manager"
        ref={wrapperRef}
        style={{ width: "100%" }}
      >
        <div
          className="session-manager__trigger"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className="session-manager__trigger-label">
            Session: {sessionName}
          </span>
          <span className="session-manager__trigger-icon">
            {isOpen ? "▲" : "▼"}
          </span>
        </div>

        {isOpen && (
          <div className="session-manager__dropdown">
            <div className="session-manager__search">
              <input
                className="session-manager__input"
                value={searchQuery}
                placeholder="Search or create..."
                autoFocus
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (
                    e.key === "Enter" &&
                    searchQuery.trim() &&
                    !exactMatchExists
                  ) {
                    handleCreateSession();
                  }
                }}
              />
            </div>

            <div className="session-manager__list">
              {filteredSessions.length > 0
                ? filteredSessions.map((session) => (
                    <div
                      key={session.sessionId}
                      className={`session-manager__item ${
                        session.sessionName === sessionName
                          ? "session-manager__item--active"
                          : ""
                      }`}
                      onClick={() => {
                        setSessionName(session.sessionName);
                        setIsOpen(false);
                      }}
                    >
                      <div className="session-manager__item-content">
                        <div className="session-manager__item-dot" />
                        <span className="session-manager__item-name">
                          {session.sessionName}
                        </span>
                      </div>
                      {sessions.length > 1 && (
                        <button
                          className="session-manager__item-delete"
                          onClick={(e) => handleDeleteSession(e, session)}
                          title={`Delete "${session.sessionName}"`}
                          aria-label={`Delete session ${session.sessionName}`}
                          disabled={!!deletingSessionName}
                        >
                          {deletingSessionName === session.sessionName ? (
                            <Spinner size="0.7rem" />
                          ) : (
                            "✕"
                          )}
                        </button>
                      )}
                    </div>
                  ))
                : !searchQuery.trim() && (
                    <div className="session-manager__empty">
                      No sessions found
                    </div>
                  )}

              {searchQuery.trim().length > 0 && !exactMatchExists && (
                <button
                  className="session-manager__create-btn"
                  onClick={handleCreateSession}
                  disabled={isCreating}
                >
                  {isCreating ? (
                    <Spinner size="1rem" />
                  ) : (
                    <span className="session-manager__create-icon">+</span>
                  )}
                  Create "{searchQuery.trim()}"
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {errorMessage && (
        <ErrorDialog onClose={() => setErrorMessage("")}>
          {errorMessage}
        </ErrorDialog>
      )}
    </>
  );
};
